import React, { useCallback, useEffect, useState } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    IconButton,
    Stack,
    Divider,
    Avatar,
    Paper,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Chip,
    OutlinedInput,
    Grid,
    Tooltip,
    alpha,
    useTheme,
    CircularProgress,
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Save as SaveIcon,
    Edit as EditIcon,
    Cancel as CancelIcon,
    Phone as PhoneIcon,
    Email as EmailIcon,
    Language as LanguageIcon,
    LocationOn as LocationIcon,
    AccountBalance as BankIcon,
    Person as PersonIcon,
    Receipt as ReceiptIcon,
    CloudUpload as UploadIcon,
    Business as BusinessIcon,
    ContactPhone as ContactIcon,
    HomeWork as AddressIcon,
    ReceiptLong as TaxIcon,
    AccountBalanceWallet as BankDetailsIcon,
    DocumentScanner as DocumentIcon,
} from '@mui/icons-material';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { toast } from 'react-toastify';
import { SettingDetails } from '../../types';
import { getSettingsApi, saveSettingsApi, updateSettingsApi } from '../../services/settings';
import { baseUrlImage } from '../../../../utils/apiClient';
import { useImageCompression } from '../../../../utils/useImageCompression';
// import { log } from 'node:console';

const BUSINESS_TYPES = ['Retail', 'Wholesale', 'Manufacture'];


const SettingPageDetails: React.FC = () => {
    const theme = useTheme();
    const { compressSingleImage } = useImageCompression();
    const [isEditing, setIsEditing] = useState(false);
    const [logoFile, setLogoFile] = useState<File | null>(null);
    const [signatureFile, setSignatureFile] = useState<File | null>(null);
    const [aadharFile, setAadharFile] = useState<File | null>(null);
    const [panFile, setPanFile] = useState<File | null>(null);
    const [logoPreview, setLogoPreview] = useState<string>('');
    const [signaturePreview, setSignaturePreview] = useState<string>('');
    // const [aadharPreview, setAadharPreview] = useState<string>('');
    const [panPreview, setPanPreview] = useState<string>('');
    const [hasData, setHasData] = useState(false);
    const [loading, setLoading] = useState(false);

    const [aadharBackPreview, setAadharBackPreview] = useState<string>('');
    // const [aadharBackFile, setAadharBackFile] = useState<File | null>(null);
    // const [panBackFile, setPanBackFile] = useState<File | null>(null);
    // const [panBackPreview, setPanBackPreview] = useState<string>('');
    const [aadharFrontFile, setAadharFrontFile] = useState(null);
    const [aadharBackFile, setAadharBackFile] = useState(null);

    const [aadharFrontPreview, setAadharFrontPreview] = useState("");
    // const [aadharBackPreview, setAadharBackPreview] = useState("");

    const { control, handleSubmit, reset, watch, formState: { errors } } = useForm<SettingDetails>({
        defaultValues: {
            businessName: "",
            businessType: [],
            contact: {
                phone: [],
                email: [],
                links: []
            },
            taxDetails: {
                gstNumber: "",
                panNumber: "",
            },
            address: [],
            bankDetails: [],
            owner: {
                ownerName: "",
                ownerContact: {
                    phone: [],
                    email: [],
                    links: []
                },
                address: []
            }
        }
    });

    // Field arrays (same as before)
    const { fields: phoneFields, append: appendPhone, remove: removePhone } = useFieldArray({
        control,
        name: 'contact.phone' as 'contact.phone'
    });
    const { fields: emailFields, append: appendEmail, remove: removeEmail } = useFieldArray({
        control,
        name: 'contact.email' as 'contact.email'
    });
    const { fields: linkFields, append: appendLink, remove: removeLink } = useFieldArray({
        control,
        name: 'contact.links' as 'contact.links'
    });
    const { fields: addressFields, append: appendAddress, remove: removeAddress } = useFieldArray({
        control,
        name: 'address' as 'address'
    });
    const { fields: bankFields, append: appendBank, remove: removeBank } = useFieldArray({
        control,
        name: 'bankDetails' as 'bankDetails'
    });
    const { fields: ownerPhoneFields, append: appendOwnerPhone, remove: removeOwnerPhone } = useFieldArray({
        control,
        name: 'owner.ownerContact.phone' as 'owner.ownerContact.phone'
    });
    const { fields: ownerEmailFields, append: appendOwnerEmail, remove: removeOwnerEmail } = useFieldArray({
        control,
        name: 'owner.ownerContact.email' as 'owner.ownerContact.email'
    });
    const { fields: ownerLinkFields, append: appendOwnerLink, remove: removeOwnerLink } = useFieldArray({
        control,
        name: 'owner.ownerContact.links' as 'owner.ownerContact.links'
    });
    const { fields: ownerAddressFields, append: appendOwnerAddress, remove: removeOwnerAddress } = useFieldArray({
        control,
        name: 'owner.address' as 'owner.address'
    });

    // const handleFileUpload = (
    //     event: React.ChangeEvent<HTMLInputElement>,
    //     setFile: React.Dispatch<React.SetStateAction<File | null>>,
    //     setPreview: React.Dispatch<React.SetStateAction<string>>
    // ) => {
    //     const file = event.target.files?.[0];
    //     if (file) {
    //         setFile(file);
    //         const reader = new FileReader();
    //         reader.onloadend = () => {
    //             setPreview(reader.result as string);
    //         };
    //         reader.readAsDataURL(file);
    //     }
    // };


    const handleFileUpload = async (
        e: any,
        setFile: any,
        setPreview: any
    ) => {
        const file = e.target.files[0];
        if (!file) return;

        // 🔥 optional validation
        if (!file.type.startsWith("image/")) {
            alert("Only image allowed");
            return;
        }

        // 🔥 compression
        const compressed = await compressSingleImage(file);

        // 🔥 state update
        setFile(compressed);
        setPreview(URL.createObjectURL(compressed));
    };

    const fetchData = useCallback(async () => {
        try {
            const res = await getSettingsApi();
            if (res.success && res?.data) {
                // Deep clone and parse any stringified fields
                const settingsData = JSON.parse(JSON.stringify(res.data));

                reset(settingsData);
                setHasData(true);

                setLogoPreview(settingsData.businessLogo ? baseUrlImage + settingsData.businessLogo : "");
                setSignaturePreview(settingsData.owner?.signature ? baseUrlImage + settingsData.owner.signature : "");
                setAadharFrontPreview(
                    settingsData.documents?.aadhar?.front
                        ? baseUrlImage + settingsData.documents.aadhar.front
                        : ""
                );
                setAadharBackPreview(
                    settingsData.documents?.aadhar?.back
                        ? baseUrlImage + settingsData.documents.aadhar.back
                        : ""
                );
                setPanPreview(settingsData.documents?.pan?.panPhoto ? baseUrlImage + settingsData.documents.pan.panPhoto : "");
            }
        } catch (err: any) {
            console.error(err);
        }
    }, [reset]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const onSubmit = async (data: SettingDetails) => {
        setLoading(true)
        try {
            const formData = new FormData();

            formData.append("businessName", data.businessName || "");
            formData.append("description", data.description || "");
            formData.append("businessType", JSON.stringify(data.businessType || []));

            // Files
            if (logoFile) formData.append("businessLogo", logoFile);
            if (signatureFile) formData.append("signature", signatureFile);
            if (aadharFrontFile) formData.append("aadharFront", aadharFrontFile);
            if (aadharBackFile) formData.append("aadharBack", aadharBackFile);
            if (panFile) formData.append("panPhoto", panFile);

            // JSON fields
            formData.append("contact", JSON.stringify(data.contact || {}));
            formData.append("address", JSON.stringify(data.address || []));
            formData.append("bankDetails", JSON.stringify(data.bankDetails || []));

            formData.append("taxDetails", JSON.stringify(data.taxDetails || {}));

            formData.append("owner", JSON.stringify(data.owner || {}));
            formData.append("documents", JSON.stringify(data.documents || {}));

            let res;

            if (hasData) {
                res = await updateSettingsApi(formData);
                if (res?.success) {
                    toast.success(res?.message)
                }
            } else {
                res = await saveSettingsApi(formData);
                if (res?.success) {
                    toast.success(res?.message)
                }
            }

            await fetchData();
            setIsEditing(false);

        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false)
        }
    };


    const handleCancel = async () => {
        setIsEditing(false);
        setLogoFile(null);
        setSignatureFile(null);
        setAadharFile(null);
        setPanFile(null);

        await fetchData(); // 🔥 restore original images

        toast.info('Changes cancelled');
    };



    const SectionHeader = ({ icon, title }: { icon: React.ReactNode; title: string }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            {icon}
            <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                {title}
            </Typography>
        </Box>
    );

    const renderSection = (title: string, icon: React.ReactNode, children: React.ReactNode) => (
        <Paper
            elevation={0}
            sx={{
                p: 3,
                mb: 1,
                borderRadius: 1,
            }}
        >
            <SectionHeader icon={icon} title={title} />
            <Divider sx={{ mb: 3 }} />
            {children}
        </Paper>
    );

    const renderField = (
        name: any,
        label: string,
        rules?: any,
        multiline?: boolean,
        rows?: number,
        type?: string
    ) => (
        <Controller
            name={name}
            control={control}
            rules={rules}
            render={({ field }) => (
                <TextField
                    {...field}
                    label={label}
                    size="small"
                    fullWidth
                    multiline={multiline}
                    rows={rows}
                    type={type || 'text'}
                    disabled={!isEditing}
                    error={!!(errors as any)[name]}
                    helperText={(errors as any)[name]?.message}
                    value={field.value || ''}
                    InputProps={{
                        readOnly: !isEditing,
                    }}
                    variant="outlined"
                />
            )}
        />
    );

    const renderArrayField = (
        fields: any[],
        renderItem: (field: any, index: number) => React.ReactNode,
        onAppend: () => void,
        title: string,
        emptyMessage: string,
        columns?: number // for grid layout
    ) => (
        <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                    {title}
                </Typography>
                {isEditing && (
                    <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={onAppend}
                        variant="outlined"
                        sx={{ textTransform: 'none', borderRadius: 1 }}
                    >
                        Add
                    </Button>
                )}
            </Box>
            {fields.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', py: 1 }}>
                    {emptyMessage}
                </Typography>
            ) : (
                <Grid container spacing={2}>
                    {fields.map((field, index) => (
                        <Grid size={{ xs: 12, md: columns === 2 ? 6 : 12 }} key={field.id}>
                            {renderItem(field, index)}
                        </Grid>
                    ))}
                </Grid>
            )}
        </Box>
    );

    return (
        <Box sx={{ maxWidth: 1400, margin: '0 auto' }}>
            {/* Header */}
            <Paper
                elevation={0}
                sx={{
                    p: 2,
                    mb: 1,
                    borderRadius: 1,
                    position: 'sticky',
                    top: 0,
                    zIndex: 10,
                    backdropFilter: 'blur(8px)',
                }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                    <Typography variant="h5" fontWeight="bold" sx={{ color: theme.palette.text.primary }}>
                        Business Settings
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {!isEditing ? (
                            <Button
                                variant="contained"
                                startIcon={<EditIcon />}
                                onClick={() => setIsEditing(true)}
                            >
                                Edit Settings
                            </Button>
                        ) : (
                            <>
                                <Button
                                    variant="outlined"
                                    startIcon={<CancelIcon />}
                                    onClick={handleCancel}
                                    size="medium"
                                    color="error"
                                    sx={{ textTransform: 'none', borderRadius: 1 }}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="contained"
                                    startIcon={<SaveIcon />}
                                    onClick={handleSubmit(onSubmit)}
                                    size="medium"
                                    disabled={loading ? true : false}
                                    sx={{ textTransform: 'none', borderRadius: 1 }}
                                >
                                    {loading ? <CircularProgress size={"20px"} sx={{ color: "white" }} aria-label="Loading…" /> : (hasData ? "Update Settings" : "Save Settings")}

                                </Button>
                            </>
                        )}
                    </Box>
                </Box>
            </Paper>

            {/* Business Information */}
            {renderSection('Business Information', <BusinessIcon color="primary" />, (
                <Grid container spacing={3} sx={{ borderRadius: 1 }}>
                    <Grid size={{ xs: 12, sm: 4, md: 3, lg: 2 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <Typography variant="caption" sx={{ mb: 0.5 }}>
                                Business Logo
                            </Typography>
                            <Box sx={{ position: 'relative' }}>
                                <Avatar
                                    src={logoPreview}
                                    sx={{
                                        width: 100,
                                        height: 100,
                                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                                        border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                    }}
                                >
                                    {control?._defaultValues?.businessName?.charAt(0)}
                                </Avatar>
                                {isEditing && (
                                    <Tooltip title="Upload logo">
                                        <IconButton
                                            size="small"
                                            component="label"
                                            sx={{
                                                position: 'absolute',
                                                bottom: -4,
                                                right: -4,
                                                bgcolor: 'background.paper',
                                                boxShadow: 1,
                                                '&:hover': { bgcolor: 'action.hover' }
                                            }}
                                        >
                                            <UploadIcon fontSize="small" />
                                            <input type="file" hidden accept="image/*" onChange={(e) => handleFileUpload(e, setLogoFile, setLogoPreview)} />
                                        </IconButton>
                                    </Tooltip>
                                )}
                                {/* {isEditing && (
                                    <IconButton
                                        onClick={() => {
                                            setLogoFile(null);
                                            setLogoPreview("");
                                        }}
                                        sx={{
                                            position: "absolute",
                                            right: -10,
                                            top: -10,
                                            background: "#fff"
                                        }}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                )} */}
                            </Box>
                            {logoFile && (
                                <Typography variant="caption" sx={{ mt: 1, fontSize: '0.7rem', textAlign: 'center' }}>
                                    {logoFile.name}
                                </Typography>
                            )}
                        </Box>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 8, md: 9, lg: 10 }}>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, md: 6 }}>
                                {renderField('businessName', 'Business Name', { required: 'Business name is required' })}
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <Controller
                                    name="businessType"
                                    control={control}
                                    render={({ field }) => (
                                        <FormControl fullWidth size="small" disabled={!isEditing}>
                                            <InputLabel>Business Type</InputLabel>
                                            <Select<string[]>
                                                {...field}
                                                multiple
                                                value={field.value || []}
                                                onChange={(e) => field.onChange(e.target.value)}
                                                input={<OutlinedInput label="Business Type" />}
                                                renderValue={(selected) => (
                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                        {selected.map((value) => (
                                                            <Chip key={value} label={value} size="small" />
                                                        ))}
                                                    </Box>
                                                )}
                                            >
                                                {BUSINESS_TYPES.map((type) => (
                                                    <MenuItem key={type} value={type}>
                                                        {type}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    )}
                                />
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                {renderField('description', 'Description', {}, true, 2)}
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            ))}

            {/* Contact Information - Bento Card */}
            {renderSection('Contact Information', <ContactIcon color="primary" />, (
                <Box>
                    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1, mb: 3 }}>
                        {isEditing && (
                            <>
                                <Button size="small" startIcon={<AddIcon />} onClick={() => appendPhone({ label: '', phoneNo: undefined })} variant="outlined" sx={{ textTransform: 'none' }}>
                                    Add Phone
                                </Button>
                                <Button size="small" startIcon={<AddIcon />} onClick={() => appendEmail({ email: "" })} variant="outlined" sx={{ textTransform: 'none' }}>
                                    Add Email
                                </Button>
                                <Button size="small" startIcon={<AddIcon />} onClick={() => appendLink({ label: '', url: '' })} variant="outlined" sx={{ textTransform: 'none' }}>
                                    Add Link
                                </Button>
                            </>
                        )}
                    </Stack>
                    <Grid container spacing={2}>
                        {phoneFields.map((field, index) => (
                            <Grid size={{ xs: 12, md: 6 }} key={field.id}>
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                    <PhoneIcon sx={{ color: 'action.active', fontSize: 20, mt: 1 }} />
                                    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
                                        <Controller
                                            name={`contact.phone.${index}.label` as const}
                                            control={control}
                                            render={({ field }) => (
                                                <TextField {...field} size="small" placeholder="Label" disabled={!isEditing} fullWidth sx={{ mb: 1 }} />
                                            )}
                                        />
                                        <Controller
                                            name={`contact.phone.${index}.phoneNo` as const}
                                            control={control}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    size="small"
                                                    placeholder="Phone Number"
                                                    type="number"
                                                    disabled={!isEditing}
                                                    fullWidth
                                                    value={field.value || ''}
                                                    onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : '')}
                                                />
                                            )}
                                        />
                                        {isEditing && (
                                            <IconButton size="small" color="error" onClick={() => removePhone(index)} sx={{ mt: 1, p: 0.5 }}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        )}
                                    </Box>
                                </Box>
                            </Grid>
                        ))}
                        {emailFields.map((field, index) => (
                            <Grid size={{ xs: 12, md: 6 }} key={field.id}>
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                    <EmailIcon sx={{ color: 'action.active', fontSize: 20, mt: 1 }} />
                                    <Box sx={{ flex: 1 }}>
                                        <Controller
                                            name={`contact.email.${index}.email` as const}
                                            control={control}
                                            render={({ field }) => (
                                                <TextField {...field} size="small" placeholder="Email" type="email" disabled={!isEditing} fullWidth />
                                            )}
                                        />
                                        {isEditing && (
                                            <IconButton size="small" color="error" onClick={() => removeEmail(index)} sx={{ mt: 1, p: 0.5 }}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        )}
                                    </Box>
                                </Box>
                            </Grid>
                        ))}
                        {linkFields.map((field, index) => (
                            <Grid size={{ xs: 12, sm: 6, md: 3 }} key={field.id}>
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                    <LanguageIcon sx={{ color: 'action.active', fontSize: 20, mt: 1 }} />
                                    <Box sx={{ flex: 1 }}>
                                        <Controller
                                            name={`contact.links.${index}.label` as const}
                                            control={control}
                                            render={({ field }) => (
                                                <TextField {...field} size="small" placeholder="Label" disabled={!isEditing} fullWidth sx={{ mb: 1 }} />
                                            )}
                                        />
                                        <Controller
                                            name={`contact.links.${index}.url` as const}
                                            control={control}
                                            render={({ field }) => (
                                                <TextField {...field} size="small" placeholder="URL" disabled={!isEditing} fullWidth />
                                            )}
                                        />
                                        {isEditing && (
                                            <IconButton size="small" color="error" onClick={() => removeLink(index)} sx={{ mt: 1, p: 0.5 }}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        )}
                                    </Box>
                                </Box>
                            </Grid>
                        ))}
                        {phoneFields.length === 0 && emailFields.length === 0 && linkFields.length === 0 && (
                            <Grid size={{ xs: 12 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center', py: 2 }}>
                                    No contact information added
                                </Typography>
                            </Grid>
                        )}
                    </Grid>
                </Box>
            ))}

            {/* Addresses */}
            {renderSection('Addresses', <AddressIcon color="primary" />, (
                <Stack spacing={2}>
                    {renderArrayField(
                        addressFields,
                        (field, index) => (
                            <Paper key={field.id} variant="outlined" sx={{ p: 2, borderRadius: 1, bgcolor: alpha(theme.palette.background.default, 0.5) }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <LocationIcon color="action" sx={{ fontSize: 20 }} />
                                        <Typography variant="subtitle2" fontWeight={500}>Address {index + 1}</Typography>
                                    </Box>
                                    {isEditing && (
                                        <IconButton size="small" color="error" onClick={() => removeAddress(index)}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    )}
                                </Box>
                                <Grid container spacing={2}>
                                    <Grid size={{ xs: 12, md: 4 }}>
                                        {renderField(`address.${index}.location` as const, 'Location Name')}
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 4 }}>
                                        {renderField(`address.${index}.line1` as const, 'Address Line')}
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 4 }}>
                                        {renderField(`address.${index}.city` as const, 'City')}
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 4 }}>
                                        {renderField(`address.${index}.state` as const, 'State')}
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 4 }}>
                                        {renderField(`address.${index}.country` as const, 'Country')}
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 4 }}>
                                        {renderField(`address.${index}.pinCode` as const, 'PIN Code')}
                                    </Grid>
                                </Grid>
                            </Paper>
                        ),
                        () => appendAddress({}),
                        'Business Addresses',
                        'No addresses added',
                        2
                    )}
                </Stack>
            ))}

            {/* Tax & Documents - Bento Grid */}
            <Grid container spacing={1} sx={{ mb: 1, borderRadius: 1 }}>
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper elevation={0} sx={{ p: 3, borderRadius: 1, height: '100%' }}>
                        <SectionHeader icon={<TaxIcon color="primary" />} title="Tax Details" />
                        <Divider sx={{ mb: 3 }} />
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12 }}>
                                {renderField('taxDetails.gstNumber', 'GST Number')}
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                {renderField('taxDetails.panNumber', 'PAN Number')}
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper elevation={0} sx={{ p: 3, borderRadius: 1, height: '100%' }}>
                        <SectionHeader icon={<DocumentIcon color="primary" />} title="Documents" />
                        <Divider sx={{ mb: 3 }} />
                        <Grid container spacing={3}>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <Paper variant="outlined" sx={{ p: 2, borderRadius: 1, height: '100%' }}>
                                    {/* <Typography variant="subtitle2" fontWeight={500} gutterBottom>Aadhar Card</Typography> */}
                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
                                        <Box sx={{ flex: 1, width: '100%' }}>
                                            {renderField('documents.aadhar.aadharNumber', 'Aadhar Number')}
                                        </Box>

                                        <Box sx={{ position: 'relative', display: "inline-block" }}>
                                            <a
                                                href={aadharFrontPreview}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                title="View Aadhar Front"
                                            >
                                                <Tooltip title={watch("documents.aadhar.aadharNumber") || ""}>

                                                    <Avatar
                                                        src={aadharFrontPreview}
                                                        variant="rounded" // ✅ FIX
                                                        sx={{ width: 60, height: 60, cursor: "pointer" }}
                                                    />
                                                </Tooltip>
                                            </a>
                                            {isEditing && (
                                                <Tooltip title="Upload Aadhar photo">
                                                    <IconButton
                                                        size="small"
                                                        component="label"
                                                        sx={{ position: 'absolute', bottom: -4, right: -4, bgcolor: 'background.paper', boxShadow: 1 }}>
                                                        <UploadIcon fontSize="small" />
                                                        {/* <input type="file" hidden accept="image/*" onChange={(e) => handleFileUpload(e, setAadharFile, setAadharPreview)} /> */}
                                                        <input type="file" hidden accept="image/*" onChange={(e) => handleFileUpload(e, setAadharFrontFile, setAadharFrontPreview)} />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {/* {isEditing && (
                                                <IconButton
                                                    onClick={() => {
                                                        setAadharFrontFile(null);
                                                        setAadharFrontPreview("");
                                                    }}
                                                    sx={{
                                                        position: "absolute",
                                                        right: -10,
                                                        top: -10,
                                                        background: "#fff"
                                                    }}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            )} */}
                                        </Box>

                                        {/* 🔥 AADHAR BACK */}
                                        <Box sx={{ position: 'relative' }}>
                                            <a
                                                href={aadharBackPreview}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                title="View Aadhar Back"
                                            >
                                                <Avatar
                                                    src={aadharBackPreview}
                                                    variant="rounded" // ✅ FIX
                                                    sx={{ width: 60, height: 60, cursor: "pointer" }}
                                                />
                                            </a>
                                            {isEditing && (
                                                <Tooltip title="Upload Aadhar Back">
                                                    <IconButton
                                                        component="label"
                                                        size="small"
                                                        sx={{ position: 'absolute', bottom: -4, right: -4, bgcolor: 'background.paper', boxShadow: 1 }}>
                                                        <UploadIcon fontSize="small" />
                                                        <input
                                                            type="file"
                                                            hidden
                                                            accept="image/*"
                                                            onChange={(e) => {
                                                                const file = e.target.files?.[0];
                                                                if (!file) return;
                                                                handleFileUpload(e, setAadharBackFile, setAadharBackPreview);
                                                            }}
                                                        />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {/* {isEditing && (
                                                <IconButton
                                                    onClick={() => {
                                                        setAadharBackFile(null);
                                                        setAadharBackPreview("");
                                                    }}
                                                    sx={{
                                                        position: "absolute",
                                                        right: -10,
                                                        top: -10,
                                                        background: "#fff"
                                                    }}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            )} */}
                                        </Box>

                                    </Stack>
                                    {aadharFile && (
                                        <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>{aadharFile.name}</Typography>
                                    )}
                                </Paper>
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <Paper variant="outlined" sx={{ p: 2, borderRadius: 1, height: '100%' }}>
                                    {/* <Typography variant="subtitle2" fontWeight={500} gutterBottom>PAN Card</Typography> */}
                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
                                        <Box sx={{ flex: 1, width: '100%' }}>
                                            {renderField('documents.pan.panNumber', 'PAN Number')}
                                        </Box>
                                        <Box sx={{ position: 'relative' }}>
                                            <a
                                                href={panPreview}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                title="View PanCard"
                                            >
                                                <Avatar
                                                    src={panPreview}
                                                    variant="rounded"
                                                    sx={{ width: 60, height: 60, bgcolor: alpha(theme.palette.primary.main, 0.1) }}
                                                >
                                                    <ReceiptIcon sx={{ fontSize: 30 }} />
                                                </Avatar>

                                            </a>
                                            {isEditing && (
                                                <Tooltip title="Upload PAN photo">
                                                    <IconButton
                                                        size="small"
                                                        component="label"
                                                        sx={{ position: 'absolute', bottom: -4, right: -4, bgcolor: 'background.paper', boxShadow: 1 }}>
                                                        <UploadIcon fontSize="small" />
                                                        <input type="file" hidden accept="image/*" onChange={(e) => handleFileUpload(e, setPanFile, setPanPreview)} />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {/* {isEditing && (
                                                <IconButton
                                                    onClick={() => {
                                                        setPanFile(null);
                                                        setPanPreview("");
                                                    }}
                                                    sx={{
                                                        position: "absolute",
                                                        right: -10,
                                                        top: -10,
                                                        background: "#fff"
                                                    }}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            )} */}
                                        </Box>
                                    </Stack>
                                    {panFile && (
                                        <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>{panFile.name}</Typography>
                                    )}
                                </Paper>
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>
            </Grid>

            {renderSection(
                'Bank Details',
                <BankDetailsIcon color="primary" />,
                (
                    <Box>
                        <Grid container spacing={3}>
                            {bankFields.length === 0 ? (
                                <Grid size={{ xs: 12 }}>
                                    <Typography color="text.secondary" sx={{ textAlign: "center" }}>
                                        No bank details added
                                    </Typography>
                                </Grid>
                            ) : (
                                // {
                                bankFields.map((field: any, index: number) => (
                                    <Grid size={{ xs: 12, md: 6 }} key={field.id}>
                                        <Paper
                                            elevation={0}
                                            sx={{
                                                p: 2,
                                                borderRadius: 1,
                                                position: 'relative',
                                                overflow: 'hidden',

                                            }}
                                        >
                                            {/* Delete button (edit mode only) */}
                                            {isEditing && (
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => removeBank(index)}
                                                    sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            )}

                                            {/* Card header */}
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                                <BankIcon color="action" sx={{ fontSize: 20 }} />
                                                <Typography variant="subtitle2" fontWeight={600} sx={{ letterSpacing: '0.5px' }}>
                                                    Bank Account {index + 1}
                                                </Typography>
                                            </Box>

                                            {/* Content */}
                                            {isEditing ? (
                                                // Edit mode: form fields
                                                <Grid container spacing={2}>
                                                    <Grid size={{ xs: 12, md: 6 }}>
                                                        {renderField(`bankDetails.${index}.bankName` as const, 'Bank Name')}
                                                    </Grid>
                                                    <Grid size={{ xs: 12, md: 6 }}>
                                                        {renderField(`bankDetails.${index}.branchName` as const, 'Branch Name')}
                                                    </Grid>
                                                    <Grid size={{ xs: 12 }}>
                                                        {renderField(`bankDetails.${index}.accountHolderName` as const, 'Account Holder Name')}
                                                    </Grid>
                                                    <Grid size={{ xs: 12, md: 6 }}>
                                                        {renderField(`bankDetails.${index}.accountNumber` as const, 'Account Number')}
                                                    </Grid>
                                                    <Grid size={{ xs: 12, md: 6 }}>
                                                        {renderField(`bankDetails.${index}.ifscCode` as const, 'IFSC Code')}
                                                    </Grid>
                                                    <Grid size={{ xs: 12 }}>
                                                        {renderField(`bankDetails.${index}.upiId` as const, 'UPI ID')}
                                                    </Grid>
                                                </Grid>
                                            ) : (
                                                // View mode: display details in a two‑column layout
                                                <Grid container spacing={2}>
                                                    <Grid size={{ xs: 12, md: 6 }}>
                                                        <Typography variant="caption" color="text.secondary">Bank Name</Typography>
                                                        <Typography variant="body2" fontWeight={500}>{field.bankName}</Typography>
                                                    </Grid>
                                                    <Grid size={{ xs: 12, md: 6 }}>
                                                        <Typography variant="caption" color="text.secondary">Branch Name</Typography>
                                                        <Typography variant="body2" fontWeight={500}>{field.branchName}</Typography>
                                                    </Grid>
                                                    <Grid size={{ xs: 12 }}>
                                                        <Typography variant="caption" color="text.secondary">Account Holder</Typography>
                                                        <Typography variant="body2" fontWeight={500}>{field.accountHolderName}</Typography>
                                                    </Grid>
                                                    <Grid size={{ xs: 12, md: 6 }}>
                                                        <Typography variant="caption" color="text.secondary">Account Number</Typography>
                                                        <Typography variant="body2" fontWeight={500}>{field.accountNumber}</Typography>
                                                    </Grid>
                                                    <Grid size={{ xs: 12, md: 6 }}>
                                                        <Typography variant="caption" color="text.secondary">IFSC Code</Typography>
                                                        <Typography variant="body2" fontWeight={500}>{field.ifscCode}</Typography>
                                                    </Grid>
                                                    <Grid size={{ xs: 12 }}>
                                                        <Typography variant="caption" color="text.secondary">UPI ID</Typography>
                                                        <Typography variant="body2" fontWeight={500}>{field.upiId}</Typography>
                                                    </Grid>
                                                </Grid>
                                            )}
                                        </Paper>
                                    </Grid>
                                ))
                                // }

                            )}
                        </Grid>
                        {isEditing && (
                            <Button
                                size="small"
                                startIcon={<AddIcon />}
                                onClick={() => appendBank({})}
                                variant="outlined"
                                sx={{ textTransform: 'none', mt: 2 }}
                            >
                                Add Bank Account
                            </Button>
                        )}
                    </Box>
                ),
            )}
            {/* Owner Information - Bento Layout */}
            <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 1 }}>
                <SectionHeader icon={<PersonIcon color="primary" />} title="Owner Information" />
                <Divider sx={{ mb: 3 }} />
                <Stack spacing={3}>
                    {/* Row 1: Identity & Signature */}
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 1, height: '100%', bgcolor: alpha(theme.palette.background.default, 0.5) }}>
                                <Typography variant="subtitle2" fontWeight={500} gutterBottom>Owner Identity</Typography>
                                <Stack spacing={2}>
                                    {renderField('owner.ownerName', 'Owner Name')}
                                </Stack>
                            </Paper>
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 1, height: '100%', bgcolor: alpha(theme.palette.background.default, 0.5) }}>
                                <Typography variant="subtitle2" fontWeight={500} gutterBottom>Signature</Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Box sx={{ position: 'relative' }}>
                                        <Avatar
                                            src={signaturePreview}
                                            sx={{ width: 60, height: 60, bgcolor: alpha(theme.palette.primary.main, 0.1), border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}` }}
                                        >
                                            <PersonIcon sx={{ fontSize: 30 }} />
                                        </Avatar>
                                        {isEditing && (
                                            <Tooltip title="Upload signature">
                                                <IconButton
                                                    size="small"
                                                    component="label"
                                                    sx={{ position: 'absolute', bottom: -4, right: -4, bgcolor: 'background.paper', boxShadow: 1 }}
                                                >
                                                    <UploadIcon fontSize="small" />
                                                    <input type="file" hidden accept="image/*" onChange={(e) => handleFileUpload(e, setSignatureFile, setSignaturePreview)} />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        {/* {isEditing && (
                                            <IconButton
                                                onClick={() => {
                                                    setSignatureFile(null);
                                                    setSignaturePreview("");
                                                }}
                                                sx={{
                                                    position: "absolute",
                                                    right: -10,
                                                    top: -10,
                                                    background: "#fff"
                                                }}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        )} */}
                                    </Box>
                                    {signatureFile && (
                                        <Typography variant="caption">{signatureFile.name}</Typography>
                                    )}
                                </Box>
                            </Paper>
                        </Grid>
                    </Grid>

                    {/* Row 2: Owner Contact */}
                    {/* Owner Contact - Three Column Layout with Labels */}
                    {/* Owner Contact - Card-Based Layout (Refined) */}
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 1, bgcolor: alpha(theme.palette.background.default, 0.5) }}>
                        <Typography variant="subtitle2" fontWeight={500} gutterBottom>Owner Contact</Typography>

                        {/* Add buttons row */}
                        <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
                            {isEditing && (
                                <>
                                    <Button size="small" startIcon={<AddIcon />} onClick={() => appendOwnerPhone({ label: '', phoneNo: undefined })} variant="outlined" sx={{ textTransform: 'none' }}>
                                        Add Phone
                                    </Button>
                                    <Button size="small" startIcon={<AddIcon />} onClick={() => appendOwnerEmail({ email: "" })} variant="outlined" sx={{ textTransform: 'none' }}>
                                        Add Email
                                    </Button>
                                    <Button size="small" startIcon={<AddIcon />} onClick={() => appendOwnerLink({ label: '', url: '' })} variant="outlined" sx={{ textTransform: 'none' }}>
                                        Add Link
                                    </Button>
                                </>
                            )}
                        </Stack>

                        <Grid container spacing={2}>
                            {/* Phone Cards */}
                            {ownerPhoneFields.map((field, index) => (
                                <Grid size={{ xs: 12, md: 4 }} key={field.id}>
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            p: 1.5,
                                            borderRadius: 1,
                                            height: '100%',

                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                            <PhoneIcon sx={{ color: 'action.active', fontSize: 20, mt: 0.5 }} />
                                            <Box sx={{ flex: 1 }}>
                                                {isEditing ? (
                                                    <Grid container spacing={1}>
                                                        <Grid size={{ xs: 5 }}>
                                                            <Controller
                                                                name={`owner.ownerContact.phone.${index}.label` as const}
                                                                control={control}
                                                                render={({ field }) => (
                                                                    <TextField
                                                                        {...field}
                                                                        size="small"
                                                                        placeholder="Label"
                                                                        disabled={!isEditing}
                                                                        fullWidth
                                                                    />
                                                                )}
                                                            />
                                                        </Grid>
                                                        <Grid size={{ xs: 7 }}>
                                                            <Controller
                                                                name={`owner.ownerContact.phone.${index}.phoneNo` as const}
                                                                control={control}
                                                                render={({ field }) => (
                                                                    <TextField
                                                                        {...field}
                                                                        size="small"
                                                                        placeholder="Phone Number"
                                                                        type="number"
                                                                        disabled={!isEditing}
                                                                        fullWidth
                                                                        value={field.value || ''}
                                                                        onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : '')}
                                                                    />
                                                                )}
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                ) : (
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                                            {field.label || 'Phone'}
                                                        </Typography>
                                                        <Typography variant="body2" fontWeight={500}>
                                                            {field.phoneNo}
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </Box>
                                            {isEditing && (
                                                <IconButton size="small" color="error" onClick={() => removeOwnerPhone(index)} sx={{ mt: -0.5 }}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            )}
                                        </Box>
                                    </Paper>
                                </Grid>
                            ))}

                            {/* Email Cards */}
                            {ownerEmailFields.map((field, index) => (
                                <Grid size={{ xs: 12, md: 4 }} key={field.id}>
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            p: 1.5,
                                            borderRadius: 1,

                                            height: '100%',

                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                            <EmailIcon sx={{ color: 'action.active', fontSize: 20, mt: 0.5 }} />
                                            <Box sx={{ flex: 1 }}>
                                                {isEditing ? (
                                                    <Controller
                                                        name={`owner.ownerContact.email.${index}.email` as const}
                                                        control={control}
                                                        render={({ field }) => (
                                                            <TextField
                                                                {...field}
                                                                size="small"
                                                                placeholder="Email"
                                                                type="email"
                                                                disabled={!isEditing}
                                                                fullWidth
                                                            />
                                                        )}
                                                    />
                                                ) : (
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                                            Email
                                                        </Typography>
                                                        <Typography variant="body2" fontWeight={500}>
                                                            {field.email}
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </Box>
                                            {isEditing && (
                                                <IconButton size="small" color="error" onClick={() => removeOwnerEmail(index)} sx={{ mt: -0.5 }}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            )}
                                        </Box>
                                    </Paper>
                                </Grid>
                            ))}

                            {/* Link Cards */}
                            {ownerLinkFields.map((field, index) => (
                                <Grid size={{ xs: 12, md: 4 }} key={field.id}>
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            p: 1.5,
                                            borderRadius: 1,

                                            height: '100%',

                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                            <LanguageIcon sx={{ color: 'action.active', fontSize: 20, mt: 0.5 }} />
                                            <Box sx={{ flex: 1 }}>
                                                {isEditing ? (
                                                    <Grid container spacing={1}>
                                                        <Grid size={{ xs: 4 }}>
                                                            <Controller
                                                                name={`owner.ownerContact.links.${index}.label` as const}
                                                                control={control}
                                                                render={({ field }) => (
                                                                    <TextField
                                                                        {...field}
                                                                        size="small"
                                                                        placeholder="Label"
                                                                        disabled={!isEditing}
                                                                        fullWidth
                                                                    />
                                                                )}
                                                            />
                                                        </Grid>
                                                        <Grid size={{ xs: 8 }}>
                                                            <Controller
                                                                name={`owner.ownerContact.links.${index}.url` as const}
                                                                control={control}
                                                                render={({ field }) => (
                                                                    <TextField
                                                                        {...field}
                                                                        size="small"
                                                                        placeholder="URL"
                                                                        disabled={!isEditing}
                                                                        fullWidth
                                                                    />
                                                                )}
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                ) : (
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                                            {field.label || 'Link'}
                                                        </Typography>
                                                        <Typography
                                                            variant="body2"
                                                            component="a"
                                                            href={field.url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            sx={{
                                                                color: 'primary.main',
                                                                textDecoration: 'none',
                                                                wordBreak: 'break-all',
                                                                display: 'inline-block',
                                                                '&:hover': { textDecoration: 'underline' }
                                                            }}
                                                        >
                                                            {field.url}
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </Box>
                                            {isEditing && (
                                                <IconButton size="small" color="error" onClick={() => removeOwnerLink(index)} sx={{ mt: -0.5 }}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            )}
                                        </Box>
                                    </Paper>
                                </Grid>
                            ))}

                            {/* Empty state */}
                            {ownerPhoneFields.length === 0 && ownerEmailFields.length === 0 && ownerLinkFields.length === 0 && (
                                <Grid size={{ xs: 12 }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center', py: 2 }}>
                                        No contact information added
                                    </Typography>
                                </Grid>
                            )}
                        </Grid>
                    </Paper>

                    {/* Row 3: Owner Addresses */}
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 1, bgcolor: alpha(theme.palette.background.default, 0.5) }}>
                        <Typography variant="subtitle2" fontWeight={500} gutterBottom>Owner Addresses</Typography>
                        {ownerAddressFields.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', py: 1 }}>
                                No addresses added
                            </Typography>
                        ) : (
                            <Grid container spacing={2}>
                                {ownerAddressFields.map((field, index) => (
                                    <Grid size={{ xs: 12, md: 6 }} key={field.id}>
                                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 1, height: '100%', bgcolor: 'background.paper' }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <LocationIcon color="action" sx={{ fontSize: 20 }} />
                                                    <Typography variant="subtitle2" fontWeight={500}>Address {index + 1}</Typography>
                                                </Box>
                                                {isEditing && (
                                                    <IconButton size="small" color="error" onClick={() => removeOwnerAddress(index)}>
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                )}
                                            </Box>
                                            <Grid container spacing={2}>
                                                <Grid size={{ xs: 12 }}>
                                                    {renderField(`owner.address.${index}.location` as const, 'Location Name')}
                                                </Grid>
                                                <Grid size={{ xs: 12 }}>
                                                    {renderField(`owner.address.${index}.line1` as const, 'Address Line')}
                                                </Grid>
                                                <Grid size={{ xs: 12, md: 6 }}>
                                                    {renderField(`owner.address.${index}.city` as const, 'City')}
                                                </Grid>
                                                <Grid size={{ xs: 12, md: 6 }}>
                                                    {renderField(`owner.address.${index}.state` as const, 'State')}
                                                </Grid>
                                                <Grid size={{ xs: 12, md: 6 }}>
                                                    {renderField(`owner.address.${index}.country` as const, 'Country')}
                                                </Grid>
                                                <Grid size={{ xs: 12, md: 6 }}>
                                                    {renderField(`owner.address.${index}.pinCode` as const, 'PIN Code')}
                                                </Grid>
                                            </Grid>
                                        </Paper>
                                    </Grid>
                                ))}
                            </Grid>
                        )}
                        {isEditing && (
                            <Button
                                size="small"
                                startIcon={<AddIcon />}
                                onClick={() => appendOwnerAddress({})}
                                variant="outlined"
                                sx={{ textTransform: 'none', mt: 2 }}
                            >
                                Add Address
                            </Button>
                        )}
                    </Paper>
                </Stack>
            </Paper>
        </Box>
    );
};

export default SettingPageDetails;